import type { AxiosInstance } from 'axios';
import type { BuildInfo, ReleaseNotesResponse } from '../types';

/**
 * Abrufe der Verwaltung, Bereich System: Enrichment, Agenten, Quellen, Audit-Log, E-Mails, Neuerungen.
 * Einmal für Web und App; die App ruft sie über `create…Service(api)` mit ihrer eigenen Axios-Instanz.
 * Typen, die es in `types.ts` noch nicht gibt, stehen hier im Bereich.
 *
 * Enrichment-Queue, Agent-Status, Agent-Läufe, Quellen und Audit-Log stehen schon in
 * `createEventService` (`eventService.ts`) und werden dort gelesen, nicht hier ein zweites Mal.
 * Hier steht nur, was bisher allein in den Web-Seiten lag (`EmailAdmin.tsx`, `EventSources.tsx`,
 * `releaseNotesService.ts`, `metaService.ts`).
 */

// ── E-Mails ──────────────────────────────────────────────────────────────────────────────────

export interface EmailLogEntry {
  id: number;
  recipient: string;
  subject: string;
  mailType: string;
  success: boolean;
  errorMessage: string | null;
  createdAt: string;
}

export interface EmailLogDetail extends EmailLogEntry {
  /** HTML oder Klartext, wie verschickt; `null`, wenn nichts gespeichert ist. */
  body: string | null;
}

export interface EmailLogPage {
  entries: EmailLogEntry[];
  total: number;
  page: number;
  size: number;
  /** Mail-Arten, die im Protokoll vorkommen (für den Filter). */
  types?: string[];
}

export interface EmailLogStats {
  last7dTotal: number;
  last7dFailed: number;
  byType30d: { type: string; total: number; failed: number; lastSentAt: string | null }[];
  topRecipients7d: { recipient: string; count: number; lastSentAt: string | null }[];
}

export interface EmailLogFilter {
  type?: string;
  recipient?: string;
  status?: 'success' | 'failed';
}

export interface EmailTypeSetting {
  mailType: string;
  premiumOnly: boolean;
  updatedAt?: string | null;
}

/**
 * Mail-Arten, die an einem Event hängen und deshalb nur für Premium-Veranstalter ausgelöst werden
 * können (`email_type_settings`, V140). Gespiegelt aus `EmailAdmin.tsx`.
 */
export const MAIL_TYPES_PREMIUM_SCHALTBAR = [
  'REGISTRATION_NOTICE', 'FAVORITE_REMINDER', 'NEW_EVENTS', 'WEEKLY_DIGEST',
  'FAVORITE_CHANGE', 'FAVORITE_NEXT_EDITION', 'FAVORITE_CANCELLATION',
];

/** Mehr Mails je Empfänger in 7 Tagen gilt als auffällig (etwa eine am Tag). */
export const MAILS_JE_EMPFAENGER_AUFFAELLIG = 7;

// ── Quellen ─────────────────────────────────────────────────────────────────────────────────

export interface ClubSyncResult { created: number; updated: number; linked: number }

export interface BreitensportRefreshResult {
  eventsScanned: number;
  eventsUpdated: number;
  eventsUnchanged: number;
  eventsDead: number;
  eventsFailed: number;
  eventsBlockedByDuplicate: number;
  distancesAdded: number;
  distancesUpdated: number;
  distanceDiscrepancies: number;
}

// ── Beschriftungen der Agenten-Seiten (bisher je Web-Seite, hier einmal) ────────────────────

export const AGENT_QUELLEN: { key: string; label: string }[] = [
  { key: 'openclaw-event-enricher', label: 'Event-Enricher' },
  { key: 'openclaw-organizer-enricher', label: 'Organizer-Enricher' },
  { key: 'openclaw-prorace-enricher', label: 'ProRace-Enricher' },
];

export const agentLabel = (key: string): string => AGENT_QUELLEN.find(q => q.key === key)?.label ?? key;

export const ENRICHMENT_STATUS_LABEL: Record<string, string> = {
  PENDING: 'wartet',
  IN_PROGRESS: 'in Arbeit',
  COMPLETED: 'erledigt',
  FAILED: 'fehlgeschlagen',
};

export const AGENT_ENTITAET_LABEL: Record<string, string> = {
  event: 'Event',
  organizer: 'Veranstalter',
  pro_race: 'Profi-Rennen',
  EVENT: 'Event',
  ORGANIZER: 'Veranstalter',
  PRO_RACE: 'Profi-Rennen',
  PRO_RACE_STAGE: 'Etappe',
};

export const AGENT_LAUF_STATUS_LABEL: Record<string, string> = {
  OK: 'fehlerfrei',
  FAILED: 'fehlgeschlagen',
  RUNNING: 'läuft',
};

export const AGENT_VERSUCH_STATUS: { key: string; label: string; hinweis: string }[] = [
  { key: 'submitted', label: 'angereichert', hinweis: 'Daten wurden ins Portal übernommen.' },
  { key: 'proposed', label: 'Vorschlag eingereicht', hinweis: 'Der Vorschlag liegt in der Prüfliste.' },
  { key: 'no_data', label: 'nichts gefunden', hinweis: 'Die Quelle hatte keine brauchbaren Daten.' },
  { key: 'low_confidence', label: 'Daten zu unsicher', hinweis: 'Zu unsicher für eine automatische Übernahme.' },
  { key: 'wrong_page', label: 'Seite passte nicht', hinweis: 'Die Veranstalterseite zeigte ein anderes Event.' },
  { key: 'error', label: 'Fehler', hinweis: 'Beim Anreichern ist ein Fehler aufgetreten.' },
  { key: 'unchanged', label: 'keine Änderung', hinweis: 'Der Datensatz war schon vollständig.' },
  { key: 'deleted', label: 'gelöscht', hinweis: 'Das Event wurde inzwischen gelöscht.' },
];

export const versuchStatusLabel = (s: string): string => AGENT_VERSUCH_STATUS.find(v => v.key === s)?.label ?? s;

const FELD_LABEL: Record<string, string> = {
  title: 'Titel', eventDate: 'Datum', status: 'Status', eventType: 'Eventart', category: 'Kategorie',
  description: 'Beschreibung', distances: 'Distanzen', routeUrl: 'Streckenlink',
  startTime: 'Startzeit', startTimeEnd: 'Startfenster', meetingPoint: 'Treffpunkt',
  street: 'Straße', houseNumber: 'Hausnummer', postalCode: 'PLZ', city: 'Ort',
  bundesland: '(Bundes)Land', fullyBooked: 'Ausgebucht-Status', tentative: 'Vorläufig-Status',
  registrationUrl: 'Anmeldelink', komootCollectionUrl: 'Komoot-Collection', flyerUrl: 'Flyer',
  organizerLogoUrl: 'Logo', organizerUrl: 'Veranstalter-Website', logoUrl: 'Logo', website: 'Website',
  email: 'E-Mail', phone: 'Telefon', name: 'Name', winner: 'Sieger', stages: 'Etappen',
  broadcastInfo: 'Sendezeit', wikipediaUrl: 'Wikipedia-Link', websiteUrl: 'Website',
};

export const agentFeldLabel = (key: string): string => FELD_LABEL[key] || key;

/** `applied_fields` eines Versuchs als Feldliste („Beschreibung, Startzeit"). */
export function uebernommeneFelder(applied: unknown): string {
  if (!applied || typeof applied !== 'object') return '';
  if (Array.isArray(applied)) return applied.map(k => agentFeldLabel(String(k))).join(', ');
  const o = applied as Record<string, unknown>;
  if (typeof o.enrichments === 'number') return `${o.enrichments} Felder ergänzt`;
  return Object.keys(o).map(agentFeldLabel).join(', ');
}

/** Die `metadata` eines Agent-Status-Eintrags in ein bis zwei lesbaren Sätzen. */
export function agentStatusText(meta: Record<string, unknown> | undefined | null): string | null {
  if (!meta) return null;
  const s: string[] = [];
  if (meta.deleted === true) s.push('Event wurde gelöscht und wird nicht mehr angefasst.');
  if (Array.isArray(meta.fields_found) && meta.fields_found.length > 0) {
    s.push(`Angereichert: ${(meta.fields_found as string[]).map(agentFeldLabel).join(', ')}.`);
  }
  if (typeof meta.proposalId === 'number') s.push(`Vorschlag #${meta.proposalId} eingereicht.`);
  if (typeof meta.enrichments === 'number' && meta.enrichments > 0) s.push(`${meta.enrichments} Felder ergänzt.`);
  if (meta.status === 'error') s.push('Beim letzten Versuch ist ein Fehler aufgetreten.');
  if (meta.status === 'wrong_page') s.push('Veranstalterseite zeigte nicht das gesuchte Event.');
  if (meta.status === 'no_data') s.push('Die Quelle bot keine brauchbaren Daten.');
  if (meta.status === 'insufficient') s.push('Die Daten waren unvollständig.');
  if (meta.status === 'low_confidence') s.push('Die Daten waren zu unsicher für eine Übernahme.');
  return s.length ? s.join(' ') : null;
}

// ── Audit-Log ───────────────────────────────────────────────────────────────────────────────

export const AUDIT_AKTION_LABEL: Record<string, string> = {
  CREATE_EVENT: 'Event angelegt',
  UPDATE_EVENT: 'Event geändert',
  DELETE_EVENT: 'Event gelöscht',
  UPDATE_STATUS: 'Status geändert',
  PROPOSE_CREATE: 'Neues Event vorgeschlagen',
  PROPOSE_EDIT: 'Bearbeitung vorgeschlagen',
  APPROVE_EDIT: 'Vorschlag genehmigt',
  REJECT_EDIT: 'Vorschlag abgelehnt',
  SYNC_CREATED: 'Sync: angelegt',
  SYNC_UPDATED: 'Sync: geändert',
  SYNC_ENRICHED: 'Sync: angereichert',
  AGENT_ENRICH: 'Agent: angereichert',
  ENQUEUE_ENRICHMENT: 'Re-Enrich angefragt',
  // Weitere Aktionen, die das Backend schreibt (im Web bisher ohne Beschriftung)
  NEW_EDITION: 'Neue Auflage',
  ROLL_FORWARD_EVENT: 'Fortgeschrieben',
  MERGE_EVENTS: 'Events zusammengeführt',
  DISMISS_DUPLICATE_GROUP: 'Kein Duplikat',
  SET_EVENT_PROMOTION: 'Hervorhebung gesetzt',
  CLEAR_EVENT_PROMOTION: 'Hervorhebung entfernt',
  UPLOAD_EVENT_IMAGE: 'Bild hochgeladen',
  DELETE_EVENT_IMAGE: 'Bild gelöscht',
  UPLOAD_EVENT_FLYER: 'Flyer hochgeladen',
  DELETE_EVENT_FLYER: 'Flyer gelöscht',
  UPLOAD_EVENT_GPX: 'GPX hochgeladen',
  PROPOSE_EVENT_IMAGE: 'Bild vorgeschlagen',
  APPROVE_EVENT_IMAGE: 'Bild angenommen',
  REJECT_EVENT_IMAGE: 'Bild abgelehnt',
  PROPOSE_IMAGE: 'Bild vorgeschlagen',
  PROPOSE_FLYER: 'Flyer vorgeschlagen',
  PROPOSE_GPX: 'GPX vorgeschlagen',
  PROPOSE_PUBLISH: 'Veröffentlichung vorgeschlagen',
  APPROVE_ALL_PENDING_EDITS: 'Alle Vorschläge genehmigt',
  REJECT_ALL_PENDING_EDITS: 'Alle Vorschläge abgelehnt',
  MERGE_EDITS: 'Vorschläge zusammengefasst',
  SUPERSEDE_EDITS: 'Vorschlag überholt',
  CREATE_ORGANIZER: 'Veranstalter angelegt',
  LINK_ORGANIZER: 'Veranstalter verknüpft',
  SET_ORGANIZER_KIND: 'Veranstalter eingeordnet',
  SET_ORGANIZER_VERIFICATION: 'Haken gesetzt',
  CLEAR_ORGANIZER_VERIFICATION: 'Haken entfernt',
  DISMISS_ORGANIZER_DUPLICATE_GROUP: 'Kein Duplikat (Veranstalter)',
  UPDATE_ORGANIZER_FROM_OUTREACH: 'Kontaktdaten aus Outreach',
  REQUEST_ORGANIZER_ACCESS: 'Zugang beantragt',
  GRANT_ORGANIZER_ACCESS: 'Zugang erteilt',
  REVOKE_ORGANIZER_ACCESS: 'Zugang entzogen',
  AUTO_APPROVE_ORGANIZER_ACCESS: 'Zugang per Domain',
  APPROVE_ORGANIZER_ACCESS_BY_EMAIL: 'Zugang vom Verein freigegeben',
  CLAIM_ORGANIZER_ACCESS_BY_TOKEN: 'Zugang per Link',
  CREATE_BOOKING_REQUEST: 'Bestellung angelegt',
  APPLY_BOOKING_REQUEST: 'Bestellung freigeschaltet',
  BOOKING_PAID: 'Zahlung erhalten',
  CREATE_NEWS_POST: 'Artikel angelegt',
  UPDATE_NEWS_POST: 'Artikel geändert',
  UPDATE_NEWS_POST_STATUS: 'Artikel-Status geändert',
  DELETE_NEWS_POST: 'Artikel gelöscht',
  UPLOAD_NEWS_COVER: 'Titelbild hochgeladen',
  DELETE_NEWS_COVER: 'Titelbild gelöscht',
  REQUEST_OUTREACH_DRAFT: 'Entwurf bestellt',
  EVENT_REFRESHED_FROM_BREITENSPORT: 'Breitensport-Abgleich',
  EVENT_SOURCE_DEAD: 'Quelle nicht mehr erreichbar',
  EVENT_SOURCE_UNPARSABLE: 'Quelle nicht lesbar',
  MIGRATE_LEGACY_IMAGE: 'Bild übernommen',
  UPDATE_EMAIL_TYPE_SETTING: 'Mail-Einstellung geändert',
  RESET_AGENT_STATE: 'Agent-Status zurückgesetzt',
};

/** Wovon ein Audit-Eintrag handelt. */
export const AUDIT_OBJEKT_LABEL: Record<string, string> = {
  EVENT: 'Event',
  EVENT_EDIT: 'Vorschlag',
  ORGANIZER: 'Veranstalter',
  BOOKING: 'Bestellung',
  NEWS_POST: 'Artikel',
  OUTREACH_CONTACT: 'Kontakt',
  USER: 'Konto',
  EMAIL_TYPE: 'Mail-Art',
  agent_enrichment_state: 'Agent-Status',
};

export const auditObjekt = (typ: string, id: number): string => `${AUDIT_OBJEKT_LABEL[typ] ?? typ} #${id}`;

/** Filterwahl wie im Web; `AGENT_ENRICH` wird nicht mehr geschrieben und fehlt deshalb hier. */
export const AUDIT_AKTION_OPTIONEN = [
  'SYNC_CREATED', 'SYNC_UPDATED', 'SYNC_ENRICHED', 'CREATE_EVENT', 'UPDATE_EVENT', 'DELETE_EVENT',
  'UPDATE_STATUS', 'APPROVE_EDIT', 'REJECT_EDIT', 'ENQUEUE_ENRICHMENT',
].map(key => ({ key, label: AUDIT_AKTION_LABEL[key] }));

export const AUDIT_AKTEUR_OPTIONEN = [
  { key: 'HUMAN', label: 'Menschen' },
  { key: 'AGENT', label: 'OpenClaw-Agenten' },
  { key: 'SYSTEM', label: 'System (Sync)' },
];

export const AUDIT_QUELLE_OPTIONEN = [
  { key: 'radsport-events', label: 'radsport-events.de' },
  { key: 'gctouren', label: 'GCTouren' },
  { key: 'strava', label: 'Strava' },
  { key: 'openclaw-event-enricher', label: 'Event-Enricher' },
  { key: 'openclaw-organizer-enricher', label: 'Organizer-Enricher' },
  { key: 'openclaw-prorace-enricher', label: 'ProRace-Enricher' },
];

// ── Neuerungen ──────────────────────────────────────────────────────────────────────────────

/** `[Handbuch: abschnitt]` am Ende einer Notiz (siehe `ReleaseNotes.tsx`). */
export const HANDBUCH_MARKE = /\s*\[Handbuch:\s*([a-z0-9-]+)\]\s*$/i;

export function teileNotiz(text: string): { text: string; handbuch: string | null } {
  const m = text.match(HANDBUCH_MARKE);
  return m ? { text: text.replace(HANDBUCH_MARKE, ''), handbuch: m[1] } : { text, handbuch: null };
}

// ── Dienst ──────────────────────────────────────────────────────────────────────────────────

export function createSystemService(api: AxiosInstance) {
  return {
    // E-Mails (ADMIN)
    emailLog: async (page: number, size: number, f: EmailLogFilter = {}): Promise<EmailLogPage> => {
      const params: Record<string, string | number> = { page, size };
      if (f.type) params.type = f.type;
      if (f.recipient && f.recipient.trim()) params.recipient = f.recipient.trim();
      if (f.status) params.status = f.status;
      return (await api.get('/notifications/admin/email-log', { params })).data;
    },
    emailLogStats: async (): Promise<EmailLogStats> =>
      (await api.get('/notifications/admin/email-log/stats')).data,
    emailLogDetail: async (id: number): Promise<EmailLogDetail> =>
      (await api.get(`/notifications/admin/email-log/${id}`)).data,
    emailTypes: async (): Promise<EmailTypeSetting[]> =>
      (await api.get('/notifications/admin/email-types')).data,
    setEmailTypePremium: async (mailType: string, premiumOnly: boolean): Promise<void> => {
      await api.put(`/notifications/admin/email-types/${mailType}`, { premiumOnly });
    },

    // Quellen (ADMIN): Läufe, die es nur im Web gab
    syncGcTourenVereine: async (): Promise<ClubSyncResult> =>
      (await api.post('/sync/gctouren-clubs')).data,
    breitensportRefresh: async (): Promise<BreitensportRefreshResult> =>
      (await api.post('/sync/refresh-breitensport')).data,

    // Neuerungen (ADMIN + MODERATOR)
    releaseNotes: async (): Promise<ReleaseNotesResponse> => (await api.get('/release-notes')).data,
    releaseNotesGelesen: async (): Promise<void> => { await api.post('/release-notes/mark-read'); },
    buildInfo: async (): Promise<BuildInfo> => (await api.get('/meta/version')).data,
  };
}

export type SystemService = ReturnType<typeof createSystemService>;
